import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import usePaymentReturn from "../usePaymentReturn";
import PaymentResultOverlay from "../PaymentResultOverlay";
import ReceiptKindChip from "../ReceiptKindChip";
import { dubaiYMD, fmtPaymentMoment } from "../../../utils/dubaiTime";
import "./clientInvoices.css";

export default function ClientInvoices() {
    const navigate = useNavigate();

    // tab: "invoices" | "payments"
    const [tab, setTab] = useState("invoices");

    const [pendingAmount, setPendingAmount] = useState(null);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [pendingError, setPendingError] = useState(false);
    // A settle_pending bank transfer is sitting in the review queue — shown as
    // a PERSISTENT banner (set locally on submit, and from the server's
    // in_review_total whenever the breakdown carries it).
    const [transferInReview, setTransferInReview] = useState(false);
    // Separate entity wallets: each entity wallet has its OWN pending pool
    // (its contact's invoices), paid from that wallet — one pill per wallet.
    const [entityPending, setEntityPending] = useState([]); // [{ id, label, total, loading, error }]

    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoicesError, setInvoicesError] = useState("");

    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsError, setPaymentsError] = useState("");


    // ---------------------------
    // Helpers
    // ---------------------------
    const formatStatus = (status) => {
        const s = String(status || "").toLowerCase();
        switch (s) {
            case "paid":
                return "Paid";
            case "pending":
            case "unpaid":
                return "Pending Payment";
            case "overdue":
                return "Overdue";
            case "partially_paid":
                return "Partially Paid";
            // A2: the server presents an invoice covered by an in-flight
            // settlement as payment_processing — the client already paid;
            // Zoho is catching up. Never a red "pending" chip.
            case "payment_processing":
                return "Payment Processing";
            // Zoho's internal lifecycle terms — "sent", "viewed", "draft" — are
            // references for OUR team, never shown to the client (owner
            // 2026-07-19). To a client they all mean "waiting to be paid".
            case "draft":
            case "sent":
            case "viewed":
                return "Awaiting Payment";
            case "void":
                return "Cancelled";
            default:
                return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
        }
    };

    const invoiceStatusClass = (status) => {
        const s = String(status || "").toLowerCase();
        if (s === "paid") return "cfinChip--green";
        if (s === "void") return "cfinChip--grey";
        if (s === "draft" || s === "sent" || s === "viewed" || s === "payment_processing") return "cfinChip--orange";
        if (s === "pending" || s === "unpaid" || s === "overdue" || s === "partially_paid") return "cfinChip--red";
        return "cfinChip--grey";
    };

    // Asia/Dubai day (item 8) — toISOString() gave the UTC day, one day off
    // for late-evening Dubai timestamps. Same yyyy-mm-dd visual.
    const formatDate = (dateString) => {
        const raw = String(dateString || "").trim();
        if (!raw) return "—";
        return dubaiYMD(raw) || raw;
    };

    const money = (n) => {
        const num = Number(n || 0);
        return `AED ${num.toFixed(2)}`;
    };

    // ---------------------------
    // Data fetchers
    // ---------------------------
    const fetchPendingTotal = useCallback(async () => {
        try {
            setPendingLoading(true);
            setPendingError(false);
            // Outstanding-invoices pending (invoices-only) — matches the Pay-pending modal.
            const res = await axios.get("/api/client-requests/invoices/pending-breakdown");
            setPendingAmount(Number(res.data?.total || 0));
            // Server-side truth for "a transfer is being reviewed" (survives
            // reloads, unlike the local submit flag). Only ever raises here —
            // the local flag persists for this session either way.
            if (Number(res.data?.pending_settlement?.amount || 0) > 0.009) setTransferInReview(true);
        } catch (e) {
            console.error("Error fetching pending total:", e);
            setPendingAmount(null);
            setPendingError(true);
        } finally {
            setPendingLoading(false);
        }
    }, []);

    const fetchEntityPending = useCallback(async () => {
        try {
            const w = await axios.get('/api/client-requests/wallet/wallets');
            const entities = (w.data?.wallets || []).filter((x) => !x.is_main);
            if (entities.length === 0) { setEntityPending([]); return; }
            setEntityPending(entities.map((x) => ({ id: x.id, label: x.entity_label || x.label, total: null, loading: true, error: false })));
            const rows = await Promise.all(entities.map(async (x) => {
                try {
                    const r = await axios.get('/api/client-requests/invoices/pending-breakdown', { params: { wallet: x.id } });
                    return { id: x.id, label: x.entity_label || x.label, total: Number(r.data?.total || 0), loading: false, error: false };
                } catch (e) {
                    return { id: x.id, label: x.entity_label || x.label, total: null, loading: false, error: true };
                }
            }));
            setEntityPending(rows);
        } catch (e) {
            setEntityPending([]);
        }
    }, []);

    const fetchInvoices = useCallback(async () => {
        try {
            setInvoicesLoading(true);
            setInvoicesError("");
            const res = await axios.get("/api/client-requests/invoices");
            setInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
        } catch (e) {
            console.error("Error fetching invoices:", e);
            setInvoices([]);
            setInvoicesError("Failed to load invoices. Please try again.");
        } finally {
            setInvoicesLoading(false);
        }
    }, []);

    const fetchPayments = useCallback(async () => {
        try {
            setPaymentsLoading(true);
            setPaymentsError("");
            const res = await axios.get("/api/client-requests/invoices/payments");
            setPayments(Array.isArray(res.data?.payments) ? res.data.payments : []);
        } catch (e) {
            console.error("Error fetching payments:", e);
            setPayments([]);
            setPaymentsError("Failed to load payments. Please try again.");
        } finally {
            setPaymentsLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchPendingTotal(); fetchEntityPending();
        fetchInvoices();
    }, [fetchPendingTotal, fetchEntityPending, fetchInvoices]);

    // --- Pay pending invoices ------------------------------------------
    // THE PAYMENT IS ITS OWN PAGE (2026-08-14). It used to be a modal here,
    // which is why this file carried the wallet split, the drift guard and the
    // rail arithmetic; all of that now lives in ClientInvoicePay, next to the
    // card form that needs it. This page's job is the list and the header.
    const goToPay = useCallback((walletId = null) => navigate(walletId ? `/client/invoices/pay?wallet=${walletId}` : "/client/invoices/pay"), [navigate]);

    // Back from the N-Genius paypage (?payment=return&ref=…) after a card
    // pending-settlement — verify + refresh the pending total and invoices.
    // The outcome renders in the full-screen PaymentResultOverlay below.
    // STILL LIVE: the redirect rail is the fallback whenever the embedded form
    // cannot mount, and older links still land here.
    const payReturn = usePaymentReturn({
        onPaid: () => { fetchPendingTotal(); fetchEntityPending(); fetchInvoices(); },
    });

    // Lazy-load payments when opening the tab (first time)
    useEffect(() => {
        if (tab !== "payments") return;
        if (payments.length > 0) return;
        fetchPayments();
    }, [tab, payments.length, fetchPayments]);

    const isMainLoading = tab === "invoices" ? invoicesLoading : paymentsLoading;
    const mainError = tab === "invoices" ? invoicesError : paymentsError;

    const headerTitle = useMemo(() => (tab === "invoices" ? "Invoices" : "Payments"), [tab]);

    const pendingLabel = useMemo(() => {
        if (pendingLoading) return "Loading…";
        if (pendingError || pendingAmount === null) return null; // retry UI renders instead
        if (pendingAmount === 0) return "No pending invoices";
        return `Pending: ${money(pendingAmount)}`;
    }, [pendingAmount, pendingLoading, pendingError]);

    return (
        <div className="cfin">
            {/* Card payment outcome (verified against the gateway, never the
                redirect) — full-screen result overlay shared by all return
                surfaces. */}
            <PaymentResultOverlay payReturn={payReturn} surface="invoices" />

            <div className="cfin__header">
                <div className="cfin__headerLeft">
                    <h1 className="cfin__title">{headerTitle}</h1>
                    <div className={`cfin__pending ${pendingAmount === 0 && !pendingLoading && !pendingError ? "is-ok" : ""}`}>
                        {pendingLabel !== null ? (
                            <strong>{pendingLabel}</strong>
                        ) : (
                            // The pending total couldn't load — say so and offer a
                            // retry instead of pretending it's still loading.
                            <button
                                type="button"
                                className="cfin__pendingRetry"
                                onClick={fetchPendingTotal}
                            >
                                Pending amount unavailable — tap to retry
                            </button>
                        )}
                        {pendingAmount > 0 && !pendingLoading && (
                            <button
                                type="button"
                                className="cfin__payPendingBtn"
                                onClick={() => goToPay(null)}
                            >
                                Pay pending amount
                            </button>
                        )}
                    </div>
                    {entityPending.map((w) => (
                        <div key={w.id} className={`cfin__pending cfin__pending--entity ${w.total === 0 && !w.loading && !w.error ? "is-ok" : ""}`}>
                            <strong>
                                {w.loading ? `${w.label}: loading…`
                                    : w.error || w.total === null ? `${w.label}: pending amount unavailable`
                                    : w.total === 0 ? `${w.label}: no pending invoices`
                                    : `${w.label}: pending ${money(w.total)}`}
                            </strong>
                            {w.total > 0 && !w.loading && (
                                <button type="button" className="cfin__payPendingBtn" onClick={() => goToPay(w.id)}>
                                    Pay from {w.label} wallet
                                </button>
                            )}
                        </div>
                    ))}
                    {transferInReview && (
                        <div className="cfin__transferNote">
                            <span className="cfin__transferNoteDot" aria-hidden="true" />
                            Transfer details submitted — we will confirm and apply your payment within 24 hours.
                        </div>
                    )}
                </div>

                <div className="cfin__headerRight">
                    <div className="cfinToggle" role="tablist" aria-label="Finance tabs">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === "invoices"}
                            className={`cfinToggle__btn ${tab === "invoices" ? "is-active" : ""}`}
                            onClick={() => setTab("invoices")}
                            disabled={isMainLoading}
                        >
                            Invoices
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === "payments"}
                            className={`cfinToggle__btn ${tab === "payments" ? "is-active" : ""}`}
                            onClick={() => setTab("payments")}
                            disabled={isMainLoading}
                        >
                            Payments
                        </button>
                    </div>
                </div>
            </div>

            {isMainLoading ? (
                <div className="cfin__loading">
                    <div className="app-loading">
                        <div className="app-spinner" />
                        <div className="app-spinner-text">Loading {tab === "invoices" ? "invoices" : "payments"}…</div>
                    </div>
                </div>
            ) : mainError ? (
                <div className="cfin__error">{mainError}</div>
            ) : tab === "invoices" ? (
                invoices.length === 0 ? (
                    <div className="cfinEmpty">
                        <div className="cfinEmpty__icon">🧾</div>
                        <div className="cfinEmpty__title">No invoices found</div>
                        <div className="cfinEmpty__text">When invoices are generated, they will appear here.</div>
                    </div>
                ) : (
                    <div className="cfin__list">
                        {invoices.map((inv) => (
                            <div key={inv.id} className="cfinCard cfinCard--split">
                                <div className="cfinCard__left">
                                    <div className="cfinCard__top">
                                        <div className="cfinCard__id">Invoice #{inv.id}</div>
                                        {/* Billing entity (multi-billing clients only —
                                            entity_label is absent for everyone else, so
                                            normal clients render exactly as before). */}
                                        {inv.entity_label ? (
                                            <span className="cfinChip cfinChip--entity">{inv.entity_label}</span>
                                        ) : null}
                                    </div>

                                    <div className="cfinCard__grid">
                                        <div className="cfinMeta">
                                            <div className="cfinMeta__label">Date</div>
                                            <div className="cfinMeta__value">{formatDate(inv.date)}</div>
                                        </div>

                                        <div className="cfinMeta">
                                            <div className="cfinMeta__label">Amount</div>
                                            <div className="cfinMeta__value">{money(inv.amount)} (inc. VAT)</div>
                                            {/* WHAT IS STILL OWED, under the total (2026-08-02).
                                                A partially paid invoice showed only its full amount,
                                                which reads as though none of it had been paid. Shown
                                                only when it differs from the total — repeating the
                                                same figure underneath would be noise. */}
                                            {typeof inv.balance === "number"
                                                && inv.balance > 0.009
                                                && inv.balance < inv.amount - 0.009 ? (
                                                <div className="cfinMeta__pending">
                                                    {money(inv.balance)} still to pay
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <div className="cfinCard__right">
                                    <span className={`cfinChip ${invoiceStatusClass(inv.status)}`}>
                                        {formatStatus(inv.status)}
                                    </span>

                                    <button className="cfinBtnPrimary" type="button" onClick={() => {
                                        /**
                                         * OPEN IT DIRECTLY — no fetch, no blob.
                                         *
                                         * The first version fetched the bytes and then called
                                         * window.open on a blob URL. Desktop was fine; PHONES
                                         * silently refused, because the open happened after an
                                         * await and was no longer inside the user's tap, so the
                                         * popup blocker ate it (2026-08-02).
                                         *
                                         * Navigating straight to the route keeps the open inside
                                         * the gesture. The session cookie rides along on a
                                         * same-origin navigation, the route's ownership check
                                         * still runs, and Content-Disposition: inline means the
                                         * browser renders it rather than downloading it.
                                         */
                                        window.open(
                                            `/api/client-requests/invoices/${inv.zoho_invoice_id}/pdf?format=pdf`,
                                            "_blank",
                                            "noopener"
                                        );
                                    }}>
                                        View PDF
                                    </button>
                                </div>

                                {/* ✅ full-width second row */}
                                <div className="cfinCard__related">
                                    <div className="cfinMeta__label">Related Bookings</div>
                                    <div className="cfinMeta__value">
                                        {inv.bookingIds && inv.bookingIds.length > 0 ? (
                                            <span className="cfinLinks">
                                                {inv.bookingIds.map((bid) => (
                                                    <a
                                                        key={bid}
                                                        href={`/client/bookings/${bid}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="cfinLink"
                                                    >
                                                        #{bid}
                                                    </a>
                                                ))}
                                            </span>
                                        ) : (
                                            <span className="cfinMuted">None</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                        ))}
                    </div>
                )
            ) : payments.length === 0 ? (
                <div className="cfinEmpty">
                    <div className="cfinEmpty__icon">💳</div>
                    <div className="cfinEmpty__title">No payments found</div>
                    <div className="cfinEmpty__text">Once payments are received, they will appear here.</div>
                </div>
            ) : (
                <div className="cfin__list">
                    {payments.map((p) => (
                        <div key={p.id} className="cfinCard cfinCard--split">
                            <div className="cfinCard__left">
                                <div className="cfinCard__top">
                                    <div className="cfinCard__id">Payment #{p.id}</div>
                                    {p.receipt_kind ? (
                                        <ReceiptKindChip kind={p.receipt_kind} />
                                    ) : p.paymentType ? (
                                        <span className="cfinChip cfinChip--type">{p.paymentType}</span>
                                    ) : null}
                                    {/* Billing entity (multi-billing clients only). */}
                                    {p.entity_label ? (
                                        <span className="cfinChip cfinChip--entity">{p.entity_label}</span>
                                    ) : null}
                                </div>

                                <div className="cfinCard__grid cfinCard__grid--payments">
                                    <div className="cfinMeta">
                                        <div className="cfinMeta__label">Date</div>
                                        <div className="cfinMeta__value">
                                            {/* Zoho's `date` is day-only; created_time is the real
                                                payment moment (2026-08-09). */}
                                            {fmtPaymentMoment(p.created_time) || formatDate(p.date)}
                                        </div>
                                    </div>

                                    <div className="cfinMeta">
                                        <div className="cfinMeta__label">Amount</div>
                                        <div className="cfinMeta__value">{money(p.amount)}</div>
                                    </div>

                                    <div className="cfinMeta cfinMeta--wide">
                                        <div className="cfinMeta__label">Method</div>
                                        <div className="cfinMeta__value">{p.paymentMethod || "—"}</div>
                                    </div>

                                    {p.bookingId ? (
                                        <div className="cfinMeta cfinMeta--wide">
                                            <div className="cfinMeta__label">Booking</div>
                                            <div className="cfinMeta__value">
                                                <button
                                                    type="button"
                                                    className="cfinLink"
                                                    onClick={() => navigate(`/client/bookings/${p.bookingId}`)}
                                                >
                                                    #{p.bookingId}
                                                </button>
                                            </div>
                                        </div>
                                    ) : p.request_id ? (
                                        // Paid for a booking request not yet approved — link to the
                                        // request; it turns into a Booking link once confirmed.
                                        <div className="cfinMeta cfinMeta--wide">
                                            <div className="cfinMeta__label">Booking Request</div>
                                            <div className="cfinMeta__value">
                                                <button
                                                    type="button"
                                                    className="cfinLink"
                                                    onClick={() => navigate(`/client/bookings/requests/${p.request_id}`)}
                                                >
                                                    REQ-{p.request_id}
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}

                                    {p.invoiceIds && p.invoiceIds.length > 0 && (
                                        <div className="cfinMeta cfinMeta--wide">
                                            <div className="cfinMeta__label">Paid Invoices</div>
                                            <div className="cfinMeta__value">
                                                <span className="cfinLinks">
                                                    {p.invoiceIds.map((invId) => (
                                                        <button
                                                            key={invId}
                                                            type="button"
                                                            className="cfinLink"
                                                            onClick={() => setTab("invoices")}
                                                            title="View your invoices"
                                                        >
                                                            #{invId}
                                                        </button>
                                                    ))}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="cfinCard__right">
                                <span className="cfinChip cfinChip--green">Received</span>

                                {/* OPENS in a new tab (2026-08-14) — the endpoint
                                    serves the PDF inline, so this is a plain link
                                    with nothing to fail silently. */}
                                <a
                                    className="cfinBtnPrimary"
                                    href={`/api/client-requests/invoices/payments/${p.zoho_payment_id}/pdf`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open Receipt
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
}
